import { Component } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";

import { BreadcrumbService } from "src/app/_core/services/breadcrumb.service";

interface Crumb {
	label: string; // clave i18n, o texto literal si raw=true
	url?: string; // si existe, navegable
	raw?: boolean; // true → no traducir (nombre de entidad)
}

/**
 * Migas de pan (ui-redesign). Dirigido por ruta: 🏠 Inicio › Sección › (actual).
 * En detalle añade un nivel más: 🏠 Inicio › Sección › Nombre de la entidad.
 * El último nivel no es navegable; los anteriores sí. Se oculta en la landing (/home).
 */
@Component({
	selector: "app-breadcrumbs",
	templateUrl: "./breadcrumbs.component.html",
	styleUrls: ["./breadcrumbs.component.scss"],
	standalone: false,
})
export class BreadcrumbsComponent {
	trail: Crumb[] = [];

	private base: Crumb[] = [];
	private isDetail = false;
	private detailLabel: string | null = null;

	private readonly SECTION: Record<string, Crumb> = {
		opportunities: { label: "header.menu.opportunities", url: "/opportunities" },
		contracts: { label: "header.menu.contracts", url: "/contracts" },
		"profit-centers": { label: "header.menu.cebes", url: "/profit-centers" },
		sites: { label: "header.menu.sites", url: "/sites" },
		centros: { label: "header.menu.sites", url: "/sites" },
		people: { label: "header.menu.contacts", url: "/people" },
		admin: { label: "header.menu.admin", url: "/admin" },
		map: { label: "header.menu.sites", url: "/sites" },
		centro: { label: "header.menu.sites", url: "/sites" },
		cebe: { label: "header.menu.contracts", url: "/contracts" },
		powerbi: { label: "header.menu.powerbi" },
		"my-profile": { label: "miPerfil.title" },
		"my-responsibilities": { label: "misResponsabilidades.title" },
	};

	/** Etiqueta del subnivel de creación por sección (/{sección}/new). */
	private readonly CREATE_LABEL: Record<string, string> = {
		contracts: "title.createContract",
		sites: "sites.createSite",
	};

	constructor(
		public readonly router: Router,
		private readonly breadcrumbService: BreadcrumbService,
	) {
		this.build(this.router.url);
		this.router.events
			.pipe(filter((e) => e instanceof NavigationEnd))
			.subscribe((e) =>
				this.build((e as NavigationEnd).urlAfterRedirects || (e as NavigationEnd).url),
			);
		this.breadcrumbService.detail$.subscribe((label) => {
			this.detailLabel = label;
			this.render();
		});
	}

	private build(url: string): void {
		const clean = (url || "").split("?")[0].split("#")[0];
		const segs = clean.split("/").filter(Boolean);
		if (!segs.length || segs[0] === "home") {
			this.base = [];
			this.isDetail = false;
			this.render();
			return;
		}
		const t: Crumb[] = [{ label: "header.menu.inicio", url: "/home" }];
		const sec = this.SECTION[segs[0]];
		if (sec) {
			t.push({ ...sec });
		}
		// Subnivel de creación (/{sección}/new): "Inicio › Sección › Crear …".
		if (sec && segs[1] === "new") {
			const createLabel = this.CREATE_LABEL[segs[0]];
			t.push(
				createLabel
					? { label: createLabel }
					: { label: "Nuevo", raw: true },
			);
		}
		this.base = t;
		// Detección de ruta de detalle (para añadir el nombre de la entidad).
		// Detalle = sección conocida seguida de un id numérico (rutas /{plural}/:id tras la
		// homogeneización), o las rutas legacy con /detail/, /centro/:id, /cebe/:id, /admin/x.
		this.isDetail =
			(!!sec && segs.length >= 2 && /^\d+$/.test(segs[1])) ||
			/\/detail\//.test(clean) ||
			/^\/centro\/[^/]+/.test(clean) ||
			/^\/cebe\/[^/]+/.test(clean) ||
			/^\/admin\/[^/]+/.test(clean);
		this.render();
	}

	private render(): void {
		const t = [...this.base];
		if (this.isDetail && this.detailLabel && t.length) {
			t.push({ label: this.detailLabel, raw: true });
		}
		this.trail = t;
	}

	go(item: Crumb): void {
		if (item.url) {
			this.router.navigate([item.url]);
		}
	}
}
